import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { User, Compass, LogOut, UserPlus, Plus, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import InviteToAppDialog from "@/components/InviteToAppDialog";
import CreateTripDialog from "@/components/CreateTripDialog";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface NavbarProps {
  onAuthClick: () => void;
  hideUntilScroll?: boolean;
}

const Navbar = ({ onAuthClick, hideUntilScroll = false }: NavbarProps) => {
  const [showNav, setShowNav] = useState(!hideUntilScroll);
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  const { toast } = useToast();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createTripDialogOpen, setCreateTripDialogOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [returningToAdmin, setReturningToAdmin] = useState(false);

  // Show navbar after scrolling past threshold
  useEffect(() => {
    if (!hideUntilScroll) {
      setShowNav(true);
      return;
    }
    const onScroll = () => {
      const shouldShow = window.scrollY > 100;
      setShowNav(shouldShow);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also listen on document scroll as fallback
    document.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("scroll", onScroll);
    };
  }, [hideUntilScroll]);

  // Check if currently impersonating (admin ID stored in localStorage)
  useEffect(() => {
    const checkImpersonation = () => {
      const adminId = localStorage.getItem("impersonating_admin_id");
      setIsImpersonating(!!adminId);
    };

    checkImpersonation();
    window.addEventListener("storage", checkImpersonation);
    return () => window.removeEventListener("storage", checkImpersonation);
  }, []);

  const handleReturnToAdmin = async () => {
    const adminUserId = localStorage.getItem("impersonating_admin_id");
    if (!adminUserId) return;

    setReturningToAdmin(true);

    try {
      const { data, error } = await supabase.functions.invoke("return-to-admin", {
        body: { adminUserId },
      });

      if (error) {
        throw error;
      }

      if (data?.token_hash) {
        localStorage.removeItem("impersonating_admin_id");
        await supabase.auth.signOut();
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: data.token_hash,
          type: "magiclink",
        });

        if (verifyError) {
          throw verifyError;
        }

        toast({
          title: t("nav.returnToAdminSuccess"),
        });

        window.location.href = getLocalizedPath("/admin");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Return to admin error:", err);
      toast({
        title: t("nav.returnToAdminError"),
        variant: "destructive",
      });
    } finally {
      setReturningToAdmin(false);
    }
  };

  const getUserInitials = () => {
    if (!user?.user_metadata?.full_name) return "U";
    const names = user.user_metadata.full_name.split(" ");
    return names.map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <nav className={`${hideUntilScroll ? "fixed" : "sticky"} top-0 left-0 right-0 z-[100] w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 transition-all duration-500 ${!showNav ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"}`}>
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Logo onClick={() => navigate(getLocalizedPath("/"))} />

        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hover:scale-105 active:scale-95 transition-transform">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(getLocalizedPath("/explore"))}
              className="gap-2 group border-primary/30 hover:border-primary hover:bg-primary/5 hover:text-foreground transition-all duration-300"
            >
              <Compass className="h-4 w-4 text-primary transition-transform duration-300 group-hover:rotate-[360deg]" />
              <span className="hidden sm:inline">{t('nav.explore')}</span>
            </Button>
          </div>
          {user ? (
            <>
              <div className="hover:scale-105 active:scale-95 transition-transform">
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => setCreateTripDialogOpen(true)}
                  className="gap-2 group shadow-sm hover:shadow-md hover:shadow-primary/20 transition-shadow duration-300"
                >
                  <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
                  <span className="hidden sm:inline">{t('nav.createTrip')}</span>
                </Button>
              </div>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">{getUserInitials()}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline">{user.user_metadata?.full_name || t('nav.profile')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isImpersonating && (
                    <>
                      <DropdownMenuItem 
                        onClick={handleReturnToAdmin}
                        disabled={returningToAdmin}
                        className="text-primary font-medium"
                      >
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        {returningToAdmin ? t('nav.returningToAdmin') : t('nav.returnToAdmin')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate(getLocalizedPath("/my-trips"))}>
                    <Compass className="h-4 w-4 mr-2" />
                    {t('nav.myTrips')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setInviteDialogOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('nav.inviteFriend')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(getLocalizedPath("/profile"))}>
                    <User className="h-4 w-4 mr-2" />
                    {t('nav.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut}>
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <div className="hover:scale-105 active:scale-95 transition-transform">
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={onAuthClick}
                  className="gap-2 group shadow-sm hover:shadow-md hover:shadow-primary/20 transition-shadow duration-300"
                >
                  <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
                  <span className="hidden sm:inline">{t('nav.createTrip')}</span>
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={onAuthClick}>
                {t('nav.login')}
              </Button>
            </>
          )}
        </div>
      </div>
      <InviteToAppDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
      <CreateTripDialog open={createTripDialogOpen} onOpenChange={setCreateTripDialogOpen} />
    </nav>
  );
};

export default Navbar;
